// Copyright (c) Cosmo Tech.
// Licensed under the MIT license.
import { useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import equal from 'deep-equal';
import { AgGridUtils } from '@cosmotech/core';
import { useAddDatasetToStore } from '../state/hooks/DatasetHooks';
import { useOrganizationId } from '../state/hooks/OrganizationHooks';
import { useCurrentScenarioData, useScenarios } from '../state/hooks/ScenarioHooks';
import { useSolution } from '../state/hooks/SolutionHooks';
import { useWorkspaceId } from '../state/hooks/WorkspaceHooks';
import { useGetAgingLawsByScenario, useSetAgingLawsQueriesResult } from '../state/wizard/WizardHooks';
import { ScenarioParametersUtils } from '../utils';
import { FileManagementUtils } from '../utils/FileManagementUtils';
import { WizardUtils } from '../utils/WizardUtils';

const getRunTemplateParametersIds = (runTemplatesParametersIdsDict, runTemplateId) => {
  return runTemplatesParametersIdsDict?.[runTemplateId] ?? [];
};

export const useUpdateParameters = () => {
  const solutionData = useSolution().data;
  const currentScenarioData = useCurrentScenarioData();
  const organizationId = useOrganizationId();
  const workspaceId = useWorkspaceId();
  const addDatasetToStore = useAddDatasetToStore();
  const scenarios = useScenarios();
  const agingLaws = useGetAgingLawsByScenario(currentScenarioData.id);
  const updateAgingLaws = useSetAgingLawsQueriesResult();
  const agingLawsParameter = useMemo(() => {
    return solutionData.parameters?.find((parameter) => parameter.options?.subType === 'AGINGLAWS');
  }, [solutionData.parameters]);
  const dataVectorParameter = useMemo(() => {
    return solutionData.parameters?.find((parameter) => parameter.options?.subType === 'DATAVECTOR');
  }, [solutionData.parameters]);

  const technicalPolicyParameter = useMemo(() => {
    return solutionData.parameters?.find((parameter) => parameter.options?.subType === 'TECHNICALPOLICY');
  }, [solutionData.parameters]);

  const { getValues, setValue } = useFormContext();
  const parametersValues = getValues();

  const runTemplateParametersIds = useMemo(
    () => getRunTemplateParametersIds(solutionData?.runTemplatesParametersIdsDict, currentScenarioData?.runTemplateId),
    [solutionData?.runTemplatesParametersIdsDict, currentScenarioData?.runTemplateId]
  );
  const parametersMetadata = useMemo(
    () => ScenarioParametersUtils.generateParametersMetadata(solutionData, runTemplateParametersIds),
    [solutionData, runTemplateParametersIds]
  );

  const getFullMetadata = useCallback(
    (parametersValues) => {
      const staticParametersList = Object.keys(parametersMetadata);
      const dynamicParametersIds = Object.keys(parametersValues).filter(
        (parameter) => !staticParametersList.includes(parameter)
      );
      const dynamicParametersMetadata = {};
      dynamicParametersIds.forEach((key) => {
        dynamicParametersMetadata[key] = {
          id: key,
          varType: '%DATASETID%',
          options: { connectorId: agingLawsParameter?.options?.connectorId },
        };
      });
      const fullMetadata = { ...parametersMetadata, ...dynamicParametersMetadata };
      return { fullMetadata, dynamicParametersIds };
    },
    [agingLawsParameter?.options?.connectorId, parametersMetadata]
  );

  const processFilesToUpload = useCallback(() => {
    const updateParameterValue = (parameterId, keyToPatch, newValue) => {
      const currentValue = getValues(parameterId);
      setValue(parameterId, { ...currentValue, [keyToPatch]: newValue });
    };
    const parametersValues = getValues();
    const { fullMetadata, dynamicParametersIds } = getFullMetadata(parametersValues);
    for (const [key, value] of Object.entries(parametersValues)) {
      if (dynamicParametersIds.includes(key) && key === 'FamilleEquipement') {
        if (!value?.rows?.length || equal(value?.rows, agingLaws[key])) {
          delete parametersValues[key];
        } else {
          const columns = WizardUtils.getAllUniqueColumns(value.rows);
          parametersValues[key].content = AgGridUtils.toCSV(value.rows, columns);
        }
      }
    }
    return FileManagementUtils.applyPendingOperationsOnFileParameters(
      organizationId,
      workspaceId,
      solutionData,
      fullMetadata,
      parametersValues,
      updateParameterValue,
      addDatasetToStore,
      currentScenarioData?.security
    );
  }, [
    getValues,
    getFullMetadata,
    organizationId,
    workspaceId,
    solutionData,
    addDatasetToStore,
    currentScenarioData?.security,
    setValue,
    agingLaws,
  ]);

  const getParametersToUpdate = useCallback(() => {
    const parametersValues = getValues();
    const currentAgingLawsParameter = currentScenarioData.parametersValues.find(
      (value) => value.parameterId === agingLawsParameter?.id
    );
    const currentDataVectorParameter = currentScenarioData.parametersValues.find(
      (value) => value.parameterId === dataVectorParameter?.id
    );
    const currentTechnicalPolicyParameter = currentScenarioData.parametersValues.find(
      (value) => value.parameterId === technicalPolicyParameter?.id
    );
    const parsedCurrentAgingLawsParameterValue = currentAgingLawsParameter?.value
      ? JSON.parse(currentAgingLawsParameter.value)
      : [];
    const parsedCurrentDataVectorParameterValue = parametersValues.DataVector
      ? JSON.parse(parametersValues.DataVector)
      : currentDataVectorParameter?.value
        ? JSON.parse(currentDataVectorParameter.value)
        : [];
    const { dynamicParametersIds } = getFullMetadata(parametersValues);
    for (const [key, value] of Object.entries(parametersValues)) {
      if (dynamicParametersIds.includes(key) && value?.id) {
        const newParameterValue = { parameterId: key, varType: '%DATASETID%', value: value.id };
        if (key === 'FamilleEquipement' && value?.content != null) {
          updateAgingLaws(currentScenarioData.id, value?.rows, key);
          const index = parsedCurrentAgingLawsParameterValue.findIndex((value) => value.parameterId === key);
          if (index !== -1) {
            parsedCurrentAgingLawsParameterValue[index] = newParameterValue;
          } else parsedCurrentAgingLawsParameterValue.push(newParameterValue);
        } else {
          const index = parsedCurrentDataVectorParameterValue.findIndex((value) => value.parameterId === key);
          if (index !== -1) {
            parsedCurrentDataVectorParameterValue[index] = {
              parameterId: key,
              varType: '%DATASETID%',
              value: value.id,
            };
          } else parsedCurrentDataVectorParameterValue.push(newParameterValue);
        }
      }
    }
    if (parsedCurrentAgingLawsParameterValue?.length > 0)
      Object.assign(parametersValues, {
        [agingLawsParameter?.id]: JSON.stringify(parsedCurrentAgingLawsParameterValue),
      });
    if (parsedCurrentDataVectorParameterValue?.length >= 0)
      Object.assign(parametersValues, {
        [dataVectorParameter?.id]: JSON.stringify(parsedCurrentDataVectorParameterValue),
      });
    if (currentTechnicalPolicyParameter?.value !== parametersValues[currentTechnicalPolicyParameter?.id]) {
      Object.assign(parametersValues, {
        [technicalPolicyParameter?.id]: currentTechnicalPolicyParameter?.value,
      });
    }
    return ScenarioParametersUtils.buildParametersForUpdate(
      solutionData,
      parametersValues,
      runTemplateParametersIds,
      currentScenarioData,
      scenarios
    );
  }, [
    agingLawsParameter?.id,
    currentScenarioData,
    dataVectorParameter?.id,
    getFullMetadata,
    getValues,
    runTemplateParametersIds,
    scenarios,
    solutionData,
    technicalPolicyParameter?.id,
    updateAgingLaws,
  ]);

  const forceUpdate =
    ScenarioParametersUtils.shouldForceScenarioParametersUpdate(
      runTemplateParametersIds,
      parametersValues,
      solutionData
    ) ||
    !currentScenarioData?.parametersValues ||
    currentScenarioData?.parametersValues.length === 0;

  return {
    runTemplateParametersIds,
    parametersMetadata,
    processFilesToUpload,
    getParametersToUpdate,
    forceUpdate,
  };
};
